import { useEffect, useState } from 'react';
import { X, Check, Globe, MessageCircle } from 'lucide-react';
import { CookieBanner } from '../components/common/CookieBanner';
import { trackWhatsAppLead } from '../lib/trackLead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GREEN = '#1A4D2E';

export default function SigLandingPage() {
  const [data, setData] = useState(null);
  const [lang, setLang] = useState(() => {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (['ca', 'es', 'en', 'fr'].includes(urlLang)) {
      localStorage.setItem('sig_lang', urlLang);
      return urlLang;
    }
    const saved = localStorage.getItem('sig_lang');
    return ['ca', 'es', 'en', 'fr'].includes(saved) ? saved : 'ca';
  });
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/sig-landing`).then(r => r.json()).then(setData).catch(() => {});
    const prevTitle = document.title;
    document.title = "Curs d'Iniciació al Golf · Ugolf Aravell Andorra";
    return () => { document.title = prevTitle; };
  }, []);

  useEffect(() => {
    const close = () => setLangOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  if (!data || !data.t) {
    return <div className="min-h-screen bg-white" data-testid="sig-landing-loading" />;
  }

  const t = data.t[lang] || data.t.ca;
  const img = data.images || {};

  const openWA = () => {
    trackWhatsAppLead('curs-iniciacio-golf', { language: lang });
    window.open(`https://wa.me/${data.whatsapp}?text=${encodeURIComponent(t.whatsapp)}`, '_blank', 'noopener');
  };

  const changeLang = (code) => {
    setLang(code);
    localStorage.setItem('sig_lang', code);
    setLangOpen(false);
    window.scrollTo({ top: 0 });
  };

  const CtaButton = ({ testid }) => (
    <button
      onClick={openWA}
      className="inline-block bg-[#1A4D2E] text-white px-10 py-4 text-sm md:text-base font-bold tracking-[0.12em] uppercase rounded-full hover:bg-[#D4AF37] transition-colors duration-200 shadow-lg"
      data-testid={testid}
    >
      {t.cta}
    </button>
  );

  const CheckList = ({ items }) => (
    <ul className="space-y-2.5 my-5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-[#333333]">
          <span className="mt-0.5 w-5 h-5 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center shrink-0">
            <Check className="w-3.5 h-3.5" style={{ color: GREEN }} />
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="min-h-screen bg-white text-[#1A1A1A]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="sig-landing">
      {/* Offer bar */}
      <div className="bg-[#1A4D2E] text-white text-center px-4 py-3 text-sm md:text-base font-semibold" data-testid="sig-offer-bar">
        {t.offerBar}
      </div>

      {/* Header: logo + lang */}
      <header className="relative flex flex-col items-center pt-6 pb-2 px-4">
        <div className="absolute right-4 top-5" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-1.5 border border-[#DDD] rounded-full px-3 py-1.5 text-xs font-semibold hover:border-[#1A4D2E] transition-colors"
            data-testid="sig-lang-btn"
          >
            <Globe className="w-3.5 h-3.5" /> {lang.toUpperCase()}
          </button>
          {langOpen && (
            <div className="absolute right-0 mt-2 bg-white border border-[#E5E5E5] rounded-xl shadow-xl overflow-hidden z-50 min-w-[130px]">
              {(data.langs || []).map((l) => (
                <button
                  key={l.code}
                  onClick={() => changeLang(l.code)}
                  className={`block w-full text-left px-4 py-2.5 text-sm hover:bg-[#F0F2EF] ${l.code === lang ? 'font-bold text-[#1A4D2E]' : ''}`}
                  data-testid={`sig-lang-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[#1A4D2E] rounded-2xl px-8 py-3">
          <img src={img.logo} alt="Ugolf Aravell Andorra" className="h-16 md:h-24 object-contain" />
        </div>
      </header>

      {/* Hero */}
      <section className="text-center px-4 pt-6 pb-10 max-w-3xl mx-auto">
        <p className="text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-[#888888]">{t.hero.kicker}</p>
        <h1 className="mt-4 text-3xl md:text-5xl italic font-semibold leading-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: GREEN }} data-testid="sig-hero-title">
          {t.hero.title}
        </h1>
        <div className="mt-8"><CtaButton testid="sig-cta-hero" /></div>
      </section>

      {/* Experience */}
      <section className="max-w-5xl mx-auto px-4 pb-14">
        <img src={img.experience} alt="" className="w-full rounded-2xl shadow-md object-cover" />
        <div className="max-w-3xl mx-auto mt-8">
          <h2 className="text-xl md:text-2xl font-bold" style={{ color: GREEN }}>{t.experience.title}</h2>
          <p className="mt-3 text-[#444444]">{t.experience.intro}</p>
          <CheckList items={t.experience.items} />
          <p className="font-bold mt-6">{t.experience.optionsTitle}</p>
          <ul className="mt-2 space-y-1.5">
            {t.experience.options.map((o, i) => (
              <li key={i} className="font-semibold text-[#333333]">— {o}</li>
            ))}
          </ul>
          <p className="mt-6 italic font-bold" style={{ color: GREEN }}>{t.experience.closing}</p>
          <div className="mt-7 text-center"><CtaButton testid="sig-cta-experience" /></div>
        </div>
      </section>

      {/* Continue */}
      <section className="bg-[#F4F6F0] py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <img src={img.continue} alt="" className="w-full rounded-2xl shadow-md object-cover" />
          <div className="max-w-3xl mx-auto mt-8">
            <h2 className="text-2xl md:text-4xl font-extrabold uppercase" style={{ color: GREEN }}>{t.continue.title}</h2>
            {t.continue.paras.map((p, i) => <p key={i} className="mt-3 text-[#444444]">{p}</p>)}
            <CheckList items={t.continue.items} />
            <p className="italic font-bold">{t.continue.closing}</p>
            <div className="mt-7 text-center"><CtaButton testid="sig-cta-continue" /></div>
          </div>
        </div>
      </section>

      {/* Pyrenees */}
      <section className="py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <img src={img.pyrenees} alt="" className="w-full rounded-2xl shadow-md object-cover" />
          <div className="max-w-3xl mx-auto mt-8">
            <h2 className="text-2xl md:text-4xl font-extrabold uppercase leading-tight" style={{ color: GREEN }}>{t.pyrenees.title}</h2>
            {t.pyrenees.paras.map((p, i) => <p key={i} className="mt-3 text-[#444444]">{p}</p>)}
            <CheckList items={t.pyrenees.items} />
            <p className="italic font-bold" style={{ color: GREEN }}>{t.pyrenees.closing}</p>
            <div className="mt-7 text-center"><CtaButton testid="sig-cta-pyrenees" /></div>
          </div>
        </div>
      </section>

      {/* Member */}
      <section className="bg-[#F4F6F0] py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <img src={img.member} alt="" className="w-full rounded-2xl shadow-md object-cover" />
          <div className="max-w-3xl mx-auto mt-8">
            <h2 className="text-2xl md:text-4xl font-extrabold uppercase" style={{ color: GREEN }}>{t.member.title}</h2>
            {t.member.paras.map((p, i) => <p key={i} className="mt-3 text-[#444444]">{p}</p>)}
            <CheckList items={t.member.includes} />
            <p className="font-bold">{t.member.quota}</p>
            <p className="mt-4 font-semibold">{t.member.extraTitle}</p>
            <CheckList items={t.member.extras} />
            <p className="italic font-bold" style={{ color: GREEN }}>{t.member.closing}</p>
            <div className="mt-7 text-center"><CtaButton testid="sig-cta-member" /></div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-14 px-4 max-w-6xl mx-auto">
        <h2 className="text-2xl md:text-4xl font-extrabold uppercase text-center mb-8" style={{ color: GREEN }}>{t.gallery.title}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="sig-gallery">
          {(img.gallery || []).map((src, i) => (
            <img key={i} src={src} alt="" className="w-full aspect-[4/3] object-cover rounded-xl" loading="lazy" />
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A4D2E] text-white py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-xl font-bold">{t.footer.contactTitle}</h3>
          <p className="mt-3 text-white/85">{t.footer.address}</p>
          <p className="mt-1 text-white/85">{t.footer.phone}</p>
          <div className="mt-8">
            <button
              onClick={openWA}
              className="inline-block bg-white text-[#1A4D2E] px-10 py-4 text-sm md:text-base font-bold tracking-[0.12em] uppercase rounded-full hover:bg-[#D4AF37] hover:text-white transition-colors duration-200"
              data-testid="sig-cta-footer"
            >
              {t.cta}
            </button>
          </div>
          <p className="mt-10 text-xs text-white/60">© {new Date().getFullYear()} UGOLF ARAVELL Andorra. {t.footer.rights}</p>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <button
        onClick={openWA}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-200"
        aria-label="WhatsApp"
        data-testid="sig-wa-float"
      >
        <MessageCircle className="w-7 h-7" />
      </button>

      <CookieBanner />
    </div>
  );
}
